import { useState, useEffect, useCallback } from 'react'
import { flushSync } from 'react-dom'

export type Theme = 'dark' | 'light'

const THEME_STORAGE_KEY = 'app-theme'

// 从 localStorage 获取主题，默认为 dark
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'dark'
  }
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
  return savedTheme || 'dark'
}

// 应用主题到 DOM（同步执行，避免延迟）
const applyTheme = (themeMode: Theme) => {
  if (typeof window === 'undefined') return
  
  const root = document.documentElement
  const themeModeValue = themeMode === 'dark' ? 'black' : 'light'
  
  // 直接同步更新，不使用 requestAnimationFrame 以避免延迟
  if (themeMode === 'dark') {
    root.classList.remove('light-theme')
    root.classList.add('dark-theme')
  } else {
    root.classList.remove('dark-theme')
    root.classList.add('light-theme')
  }
  root.setAttribute('theme-mode', themeModeValue)
}

const useThemeModel = () => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  // 初始化时应用主题（只在首次加载时执行）
  useEffect(() => {
    applyTheme(theme)
  }, []) // 空依赖数组，只在组件挂载时执行一次

  // 切换主题（优化版本：同步更新状态和 DOM）
  const toggleTheme = useCallback(() => {
    const newTheme: Theme = theme === 'dark' ? 'light' : 'dark'
    
    // 立即更新 localStorage
    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    // 立即应用主题到 DOM（不等待 React 状态更新）
    applyTheme(newTheme)
    
    // 使用 flushSync 同步更新 React 状态，确保组件立即重新渲染
    flushSync(() => {
      setTheme(newTheme)
    })
  }, [theme])

  // 设置主题
  const setThemeMode = useCallback((newTheme: Theme) => {
    // 立即更新 localStorage
    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    // 立即应用主题到 DOM
    applyTheme(newTheme)
    
    // 同步更新 React 状态
    flushSync(() => {
      setTheme(newTheme)
    })
  }, [])

  return {
    theme,
    toggleTheme,
    setThemeMode,
  }
}

export default useThemeModel
